import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import ActivityBlock from './ActivityBlock';
import { MOCK_PROJECTS, MOCK_TASKS } from '../plannerMockData';
import {
  WEEKDAY_LABELS,
  GRID_START_MINUTES,
  GRID_END_MINUTES,
  PIXELS_PER_MINUTE,
  DEFAULT_SCROLL_MINUTES,
  clamp,
  snapToInterval,
  getDayKey,
  isDateToday,
  minutesFromDate,
  minutesToTimeLabel,
} from '../plannerUtils';

/** Live clock for the "now" indicator line — ticks once a minute, no timers left running on unmount. */
const useNowMarker = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return { dayKey: getDayKey(now), minutes: minutesFromDate(now) };
};

export const getActivityLabel = (activity) => {
  if (activity.type === 'Project') {
    const project = MOCK_PROJECTS.find((p) => p.id === activity.projectId);
    return project ? project.name : 'Project';
  }
  if (activity.type === 'Task') {
    const task = MOCK_TASKS.find((t) => t.id === activity.taskId);
    return task ? task.name : 'Task';
  }
  return activity.type;
};

const HOUR_MARKS = [];
for (let m = GRID_START_MINUTES; m <= GRID_END_MINUTES; m += 60) HOUR_MARKS.push(m);

const formatHourMark = (m) => {
  const hour = m === GRID_END_MINUTES ? 24 : Math.floor(m / 60);
  return `${String(hour).padStart(2, '0')}:00`;
};

const GRID_HEIGHT = (GRID_END_MINUTES - GRID_START_MINUTES) * PIXELS_PER_MINUTE;

const WeeklyGrid = ({
  weekDates,
  activities,
  intervalMinutes,
  onIntervalChange,
  readOnly,
  onActivityMove,
  onSlotClick,
  onActivityClick,
  onActivityClone,
}) => {
  const dayColRefs = useRef([]);
  const dragStateRef = useRef(null);
  const viewportRef = useRef(null);
  const [liveDrag, setLiveDrag] = useState(null);
  const nowMarker = useNowMarker();

  // Land the viewport on the working day (9 AM) rather than midnight on first render.
  // useLayoutEffect applies this before the browser paints, so there's no visible flash at 00:00.
  useLayoutEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = (DEFAULT_SCROLL_MINUTES - GRID_START_MINUTES) * PIXELS_PER_MINUTE;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activitiesByDay = useMemo(() => {
    const map = {};
    weekDates.forEach((d) => {
      map[getDayKey(d)] = activities.filter((a) => a.day === getDayKey(d));
    });
    return map;
  }, [activities, weekDates]);

  const endDrag = useCallback(
    (commit) => {
      const drag = dragStateRef.current;
      if (drag && commit && drag.hasMoved) {
        onActivityMove(drag.id, {
          day: drag.currentDay,
          startMinutes: drag.currentStart,
          endMinutes: drag.currentEnd,
        });
      }
      dragStateRef.current = null;
      setLiveDrag(null);
      window.removeEventListener('mousemove', handleWindowMouseMoveRef.current);
      window.removeEventListener('mouseup', handleWindowMouseUpRef.current);
    },
    [onActivityMove]
  );

  const handleWindowMouseMoveRef = useRef();
  const handleWindowMouseUpRef = useRef();

  const DRAG_THRESHOLD_PX = 4;

  const beginDrag = (e, activity, mode) => {
    if (readOnly) return;
    dragStateRef.current = {
      id: activity.id,
      mode,
      startClientY: e.clientY,
      startClientX: e.clientX,
      hasMoved: false,
      originalDay: activity.day,
      originalStart: activity.startMinutes,
      originalEnd: activity.endMinutes,
      currentDay: activity.day,
      currentStart: activity.startMinutes,
      currentEnd: activity.endMinutes,
    };
    // liveDrag is intentionally not set here — a plain click (no movement) must leave the
    // original block mounted so the browser's click event still lands on it.

    handleWindowMouseMoveRef.current = (moveEvt) => {
      const drag = dragStateRef.current;
      if (!drag) return;

      if (!drag.hasMoved) {
        const movedPx = Math.hypot(moveEvt.clientX - drag.startClientX, moveEvt.clientY - drag.startClientY);
        if (movedPx < DRAG_THRESHOLD_PX) return;
        drag.hasMoved = true;
      }

      const deltaYRaw = (moveEvt.clientY - drag.startClientY) / PIXELS_PER_MINUTE;
      const deltaMinutes = snapToInterval(deltaYRaw, intervalMinutes);
      const duration = drag.originalEnd - drag.originalStart;

      let newDay = drag.originalDay;
      let newStart = drag.originalStart;
      let newEnd = drag.originalEnd;

      if (drag.mode === 'move') {
        newStart = clamp(
          drag.originalStart + deltaMinutes,
          GRID_START_MINUTES,
          GRID_END_MINUTES - duration
        );
        newEnd = newStart + duration;

        const col = dayColRefs.current.find((ref) => {
          if (!ref) return false;
          const rect = ref.getBoundingClientRect();
          return moveEvt.clientX >= rect.left && moveEvt.clientX <= rect.right;
        });
        if (col) newDay = col.dataset.day;
      } else if (drag.mode === 'resize-bottom') {
        newEnd = clamp(drag.originalEnd + deltaMinutes, drag.originalStart + intervalMinutes, GRID_END_MINUTES);
      } else if (drag.mode === 'resize-top') {
        newStart = clamp(drag.originalStart + deltaMinutes, GRID_START_MINUTES, drag.originalEnd - intervalMinutes);
      }

      drag.currentDay = newDay;
      drag.currentStart = newStart;
      drag.currentEnd = newEnd;
      setLiveDrag({ id: drag.id, day: newDay, start: newStart, end: newEnd });
    };

    handleWindowMouseUpRef.current = () => endDrag(true);

    window.addEventListener('mousemove', handleWindowMouseMoveRef.current);
    window.addEventListener('mouseup', handleWindowMouseUpRef.current);
  };

  const handleColumnClick = (e, dayKey) => {
    if (readOnly) return;
    if (e.target !== e.currentTarget) return; // ignore bubbled clicks from blocks
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const rawMinutes = GRID_START_MINUTES + offsetY / PIXELS_PER_MINUTE;
    const startMinutes = clamp(
      snapToInterval(rawMinutes, intervalMinutes),
      GRID_START_MINUTES,
      GRID_END_MINUTES - 60
    );
    onSlotClick(dayKey, startMinutes);
  };

  return (
    <Box className="planner-grid-toolbar">
      <FormControl size="small" sx={{ minWidth: 160, mb: 1.5 }}>
        <InputLabel id="planner-interval-label">Planning interval</InputLabel>
        <Select
          labelId="planner-interval-label"
          label="Planning interval"
          value={intervalMinutes}
          onChange={(e) => onIntervalChange(Number(e.target.value))}
        >
          <MenuItem value={10}>10 minutes</MenuItem>
          <MenuItem value={20}>20 minutes</MenuItem>
          <MenuItem value={30}>30 minutes</MenuItem>
        </Select>
      </FormControl>

      <Box className="planner-grid-viewport" ref={viewportRef}>
        <Box className="planner-grid" sx={{ height: GRID_HEIGHT + 40 }}>
          <Box className="planner-time-axis">
            <Box className="planner-grid-header-spacer" />
            {HOUR_MARKS.map((m) => (
              <Typography
                key={m}
                variant="caption"
                className="planner-time-label"
                style={{ top: (m - GRID_START_MINUTES) * PIXELS_PER_MINUTE + 40 }}
              >
                {formatHourMark(m)}
              </Typography>
            ))}
          </Box>

          {weekDates.map((date, index) => {
            const dayKey = getDayKey(date);
            const dayActivities = activitiesByDay[dayKey] || [];
            const isToday = isDateToday(date);
            const showNowLine =
              nowMarker.dayKey === dayKey &&
              nowMarker.minutes >= GRID_START_MINUTES &&
              nowMarker.minutes <= GRID_END_MINUTES;
            return (
              <Box key={dayKey} className={`planner-day-column-wrap${isToday ? ' is-today' : ''}`}>
                <Box className={`planner-day-header${isToday ? ' is-today' : ''}`}>
                  <Typography variant="subtitle2">{WEEKDAY_LABELS[index]}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {date.getDate().toString().padStart(2, '0')}{' '}
                    {date.toLocaleString('en-GB', { month: 'short' })}
                  </Typography>
                </Box>
                <Box
                  ref={(el) => {
                    dayColRefs.current[index] = el;
                  }}
                  data-day={dayKey}
                  className={`planner-day-column${readOnly ? ' readonly' : ''}`}
                  style={{ height: GRID_HEIGHT }}
                  onClick={(e) => handleColumnClick(e, dayKey)}
                >
                  {HOUR_MARKS.map((m) => (
                    <div
                      key={m}
                      className="planner-hour-line"
                      style={{ top: (m - GRID_START_MINUTES) * PIXELS_PER_MINUTE }}
                    />
                  ))}
                  {showNowLine && (
                    <div
                      className="planner-now-line"
                      style={{ top: (nowMarker.minutes - GRID_START_MINUTES) * PIXELS_PER_MINUTE }}
                    >
                      <span className="planner-now-dot" />
                      <span className="planner-now-label">{minutesToTimeLabel(nowMarker.minutes)}</span>
                    </div>
                  )}
                  {dayActivities
                    .filter((a) => !(liveDrag && liveDrag.id === a.id))
                    .map((activity) => (
                      <ActivityBlock
                        key={activity.id}
                        activity={activity}
                        gridStartMinutes={GRID_START_MINUTES}
                        label={getActivityLabel(activity)}
                        readOnly={readOnly}
                        onSelect={onActivityClick}
                        onMoveStart={(e, act) => beginDrag(e, act, 'move')}
                        onResizeStart={(e, act, edge) => beginDrag(e, act, edge === 'top' ? 'resize-top' : 'resize-bottom')}
                        onClone={onActivityClone}
                      />
                    ))}
                  {liveDrag && liveDrag.day === dayKey && (() => {
                    const original = activities.find((a) => a.id === liveDrag.id);
                    if (!original) return null;
                    return (
                      <ActivityBlock
                        key={`${liveDrag.id}-live`}
                        activity={{ ...original, startMinutes: liveDrag.start, endMinutes: liveDrag.end, day: liveDrag.day }}
                        gridStartMinutes={GRID_START_MINUTES}
                        label={getActivityLabel(original)}
                        readOnly={readOnly}
                        isDragging
                        onSelect={() => {}}
                        onMoveStart={() => {}}
                        onResizeStart={() => {}}
                        onClone={() => {}}
                      />
                    );
                  })()}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default WeeklyGrid;
